import type { LowCreditsBannerProps } from "../components/LowCreditsBanner/LowCreditsBanner";
import type { LowQuotaBannerDestinationType } from "../types";

import { useLowCreditsBannerVisibility } from "./useLowCreditsBannerVisibility";
import { useLowQuotaBannerManagement } from "./useLowQuotaBannerManagement";

type HookParams = {
    totalQuota: number;
    remainingQuota: number;
    pricePerRecord?: number;
    minOptionValue?: number;
    maxOptionValue?: number;
    type?: LowQuotaBannerDestinationType;
};

type HookValue = {
    isVisible: boolean;
    bannerConfigs: LowCreditsBannerProps;
};

/**
 * Decides both whether the low-credits banner is shown and which copy it uses, from one quota state.
 * Visibility follows the largest option (no-touch upsell); "not enough credits" copy follows the smallest.
 */
export const useLowCreditsBanner = ({
    totalQuota,
    remainingQuota,
    pricePerRecord,
    minOptionValue,
    maxOptionValue,
    type,
}: HookParams): HookValue => {
    const isVisible = useLowCreditsBannerVisibility({
        totalQuota,
        remainingQuota,
        pricePerRecord,
        maxOptionValue,
    });

    const requiredCredits =
        pricePerRecord !== undefined && minOptionValue !== undefined ? pricePerRecord * minOptionValue : undefined;

    const { lowCreditsBannerConfigs } = useLowQuotaBannerManagement({ type, requiredCredits, remainingQuota });

    return { isVisible, bannerConfigs: lowCreditsBannerConfigs };
};
